import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export function TrainerMagicLogin() {
  const { token } = useParams<{ token: string }>();
  const { signInAsTrainer, userData } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleTrainerLogin = useCallback(
    async (secretToken: string) => {
      try {
        await signInAsTrainer(secretToken);
        // Navigation will happen after auth state updates
      } catch (err: unknown) {
        console.error(err);
        setError('Invalid trainer link. Please contact your administrator.');
        setLoading(false);
      }
    },
    [signInAsTrainer]
  );

  useEffect(() => {
    if (userData?.role === 'trainer') {
      navigate('/trainer');
      return;
    }

    if (token) {
      handleTrainerLogin(token);
    } else {
      setError('Invalid trainer link');
      setLoading(false);
    }
  }, [handleTrainerLogin, navigate, token, userData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-400">Authenticating trainer access...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card variant="elevated" padding="lg" className="shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600 dark:text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Access Denied</h2>
              <p className="text-gray-600 dark:text-slate-400 mb-6">{error}</p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return null;
}
